import { computeRemainingQuota } from "@/lib/billing/computeRemainingQuota";
import { isPaidSubscription, SubscriptionLike } from "@/lib/quota/isPaidSubscription";

/** Default plan processing budget when the subscription doesn't specify one (10 hours). */
const DEFAULT_PROCESSING_SECONDS = 36000;
/** Default clip cap for the usage display when the plan doesn't specify one. */
const DEFAULT_MAX_CLIPS = 100;

export interface ProcessingUsageSubscriptionLike extends SubscriptionLike {
  current_month_processing_seconds?: number | null;
  current_month_clips?: number | null;
  plan?:
    | (SubscriptionLike["plan"] & {
        processing_seconds?: number | null;
        max_clips?: number | null;
      })
    | null;
}

export interface ProcessingUsage {
  currentMonthProcessingSeconds: number;
  planProcessingSeconds: number;
  usagePercentage: number;
  remainingProcessingSeconds: number;
  currentMonthClips: number;
  maxClips: number;
  clipsPercentage: number;
  isPaidPlan: boolean;
  /** True when the effective project duration would overrun the remaining processing quota. */
  exceedsProcessingLimit: boolean;
}

/**
 * Derives the processing-time and clip usage figures shown in the upload modals
 * (regular project upload and the news-PDF upload), plus whether a project of
 * `effectiveDurationSeconds` would exceed the remaining monthly processing quota.
 *
 * Mirrors the backend gate in `project.js#myCreate` (`Not enough processing
 * seconds`) so the UI can warn before the file is even uploaded. Pure: pass in
 * the `myData.subscription` object and the video's duration in seconds.
 */
export function computeProcessingUsage(
  subscription: ProcessingUsageSubscriptionLike | null | undefined,
  effectiveDurationSeconds: number,
): ProcessingUsage {
  const currentMonthProcessingSeconds = subscription?.current_month_processing_seconds || 0;
  const planProcessingSeconds = subscription?.plan?.processing_seconds || DEFAULT_PROCESSING_SECONDS;
  const usagePercentage =
    planProcessingSeconds > 0 ? (currentMonthProcessingSeconds / planProcessingSeconds) * 100 : 0;
  const remainingProcessingSeconds = computeRemainingQuota(
    planProcessingSeconds,
    currentMonthProcessingSeconds,
  );

  const currentMonthClips = subscription?.current_month_clips || 0;
  const rawMaxClips = subscription?.plan?.max_clips;
  const maxClips = rawMaxClips || DEFAULT_MAX_CLIPS;
  // Only render a real percentage when the plan actually defines a cap (matches
  // the original inline guard in UploadModal — no cap ⇒ empty bar, not full).
  const clipsPercentage = currentMonthClips && rawMaxClips ? (currentMonthClips / rawMaxClips) * 100 : 0;

  return {
    currentMonthProcessingSeconds,
    planProcessingSeconds,
    usagePercentage,
    remainingProcessingSeconds,
    currentMonthClips,
    maxClips,
    clipsPercentage,
    isPaidPlan: isPaidSubscription(subscription),
    exceedsProcessingLimit: effectiveDurationSeconds > remainingProcessingSeconds,
  };
}
